import React, { useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Controller, useFormContext } from 'react-hook-form';
import { Typography } from '@material-tailwind/react';
import { findInputError, isFormInvalid } from '../utils';

interface FormColorPickerProps {
    name: string;
    label?: string;
    required?: boolean;
    wrapperClass?: string;
    validation?: Record<string, any>;
}

const FormColorPicker: React.FC<FormColorPickerProps> = ({
    name,
    label,
    required,
    wrapperClass = '',
    validation
}) => {
    const [showPicker, setShowPicker] = useState(false);
    const {
        control,
        formState: { errors }
    } = useFormContext();

    const inputErrors = findInputError(errors, name);
    const isInvalid = isFormInvalid(inputErrors);

    return (
        <div className={wrapperClass}>
            {label && (
                <Typography
                    variant="small"
                    className={`mb-2 font-medium ${isInvalid ? 'text-red-500' : 'text-foreground'}`}
                >
                    {label}
                    {required && <span className="text-red-500">*</span>}
                </Typography>
            )}

            <Controller
                name={name}
                control={control}
                rules={validation}
                render={({ field: { value, onChange } }) => (
                    <div className="relative">
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setShowPicker(!showPicker)}
                                className="w-8 h-8 rounded border border-gray-300 cursor-pointer transition-transform hover:scale-110"
                                style={{ backgroundColor: value || '#000000' }}
                            />
                            <input
                                type="text"
                                value={value || ''}
                                onChange={(e) => {
                                    onChange(e.target.value);
                                    setShowPicker(false);
                                }}
                                className="w-full aria-disabled:cursor-not-allowed outline-none focus:outline-none text-black dark:text-white placeholder:text-foreground/60 bg-transparent ring-transparent border border-surface transition-all duration-300 ease-in disabled:opacity-50 disabled:pointer-events-none data-[error=true]:border-error data-[success=true]:border-success select-none data-[shape=pill]:rounded-full text-base rounded-lg py-3 leading-none px-3 ring-4 shadow-sm data-[icon-placement=start]:ps-11 data-[icon-placement=end]:pe-11 hover:border-primary hover:ring-primary/10 focus:border-primary focus:ring-primary/10 peer"
                                placeholder="#000000"
                            />
                        </div>

                        {showPicker && (
                            <div className="absolute z-50 mt-2">
                                <div
                                    className="fixed inset-0"
                                    onClick={() => setShowPicker(false)}
                                />
                                <div className="relative">
                                    <HexColorPicker
                                        color={value || '#000000'}
                                        onChange={(newColor) => {
                                            onChange(newColor);
                                        }}
                                        className="shadow-lg rounded-lg"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            />

            {isInvalid && (
                <Typography
                    variant="small"
                    color="error"
                    className="mt-1 flex items-center gap-1 text-xs font-normal"
                >
                    {inputErrors.error.message}
                </Typography>
            )}
        </div>
    );
};

export default FormColorPicker;
